import {useCallback,useEffect,useState} from "react";
import api,{formatApiErrorDetail} from "@/lib/api";
const cls="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm";
const btn="rounded-lg border px-4 py-2 text-sm font-semibold disabled:opacity-40";
const initial={campaign_id:"",binding_id:"",copy_asset_id:"",media_id:"",variant_index:0,
  destination_url:"",daily_budget_cents:1000,review_reference:"",confirm_paused_resources_only:true};

export default function MetaLeadAdStager({campaigns=[]}){
 const [form,setForm]=useState(initial),[bindings,setBindings]=useState([]),[assets,setAssets]=useState([]),
  [media,setMedia]=useState([]),[stages,setStages]=useState([]),[cap,setCap]=useState(null),
  [plan,setPlan]=useState(null),[busy,setBusy]=useState(false),[error,setError]=useState(""),[note,setNote]=useState("");
 const campaign=campaigns.find(c=>c.id===form.campaign_id);
 const change=(k,v)=>{setForm(x=>({...x,[k]:v}));setPlan(null)};
 const refresh=useCallback(async()=>{
  const results=await Promise.all([api.get("/platform/marketing/meta-lead-ads/status"),
   api.get("/platform/meta-leads/bindings"),api.get("/platform/marketing/meta-lead-ads/staged")]);
  setCap(results[0].data);setBindings(results[1].data||[]);setStages(results[2].data||[]);
 },[]);
 useEffect(()=>{refresh().catch(()=>setError("Meta ad staging status unavailable."));},[refresh]);
 useEffect(()=>{
  if(!campaign?.draft_id){setAssets([]);setMedia([]);return;}
  let alive=true;
  Promise.all([api.get("/platform/campaign-director/drafts/"+campaign.draft_id+"/assets"),
   api.get("/platform/marketing/media/drafts/"+campaign.draft_id)])
   .then(([a,b])=>{if(alive){setAssets(a.data||[]);setMedia(b.data||[]);}})
   .catch(()=>{if(alive)setError("Creative library unavailable.");});
  return()=>{alive=false};
 },[campaign?.draft_id]);
 const approvedCopy=assets.filter(a=>a.status==="approved"&&["facebook","instagram"].includes(a.channel));
 const approvedImages=media.filter(m=>m.review_approved&&m.kind==="image"&&
  ["image/png","image/jpeg"].includes(m.media_type));
 const bindingsHere=bindings.filter(b=>b.enabled&&b.campaign_id===form.campaign_id);
 const selectedCopy=approvedCopy.find(a=>a.id===form.copy_asset_id);
 const ready=form.campaign_id&&form.binding_id&&form.copy_asset_id&&form.media_id&&
  form.review_reference.trim().length>=8&&/^https:\/\//.test(form.destination_url)&&
  form.daily_budget_cents>=500&&form.daily_budget_cents<=50000;
 const preview=async()=>{
  setBusy(true);setError("");setNote("");setPlan(null);
  try{
   const r=await api.post("/platform/marketing/meta-lead-ads/preview",form);
   setPlan(r.data);setNote("Validated locally. Nothing sent to Meta.");
  }catch(e){setError(formatApiErrorDetail(e?.response?.data?.detail)||"Preview failed.");}
  finally{setBusy(false);}
 };
 const stage=async()=>{
  if(!window.confirm("Create the entire reviewed Meta LEADS campaign PAUSED? This creates external resources but cannot begin serving ads. Partial provider failures require manual reconciliation."))return;
  setBusy(true);setError("");setNote("");
  try{
   await api.post("/platform/marketing/meta-lead-ads/stage",form);
   setPlan(null);setNote("PAUSED campaign, ad set, creative and ad created. Inspect Meta Ads Manager before activating.");await refresh();
  }catch(e){setError(formatApiErrorDetail(e?.response?.data?.detail)||"Stage incomplete. Check history before retry.");await refresh().catch(()=>{});}
  finally{setBusy(false);}
 };
 return <section className="space-y-4 rounded-xl border bg-white p-4" data-testid="meta-lead-ad-stager">
  <div>
   <h2 className="text-lg font-bold">Meta Instant Form lead-ad builder</h2>
   <p className="mt-1 text-sm text-slate-600">Only reviewed campaigns, Meta forms, copy and images can be used. Preview makes no provider changes. Staging creates all resources PAUSED; advertising spend remains disabled.</p>
   <p className="mt-2 text-xs">Configuration: {cap?.configured?"Present":"Incomplete"} · Paused staging: {cap?.staging_enabled?"Enabled":"Disabled"} · Live ads: Disabled</p>
  </div>
  {error&&<p className="rounded border border-red-300 p-2 text-sm text-red-700" role="alert">{error}</p>}
  {note&&<p className="rounded border p-2 text-sm" role="status">{note}</p>}
  <div className="grid gap-3 sm:grid-cols-2">
   <label className="text-sm">Reviewed campaign
    <select className={cls} value={form.campaign_id} onChange={e=>{setForm({...initial,campaign_id:e.target.value});setPlan(null);}}>
     <option value="">Choose campaign</option>
     {campaigns.filter(c=>["active","draft"].includes(c.status)&&c.compliance_review_attested).map(c=>
      <option key={c.id} value={c.id}>{c.campaign_name||c.id}</option>)}
    </select>
   </label>
   <label className="text-sm">Approved Page / Meta Instant Form
    <select className={cls} value={form.binding_id} onChange={e=>change("binding_id",e.target.value)}>
     <option value="">Choose linked form</option>
     {bindingsHere.map(b=><option key={b.id} value={b.id}>Page {b.page_id} · Form {b.form_id}</option>)}
    </select>
   </label>
   <label className="text-sm">Approved Facebook/Instagram copy
    <select className={cls} value={form.copy_asset_id} onChange={e=>{change("copy_asset_id",e.target.value);setForm(x=>({...x,variant_index:0}));}}>
     <option value="">Choose approved copy</option>
     {approvedCopy.map(a=><option key={a.id} value={a.id}>{a.channel} · {a.id.slice(0,12)}</option>)}
    </select>
   </label>
   <label className="text-sm">Copy variant
    <select className={cls} value={form.variant_index} onChange={e=>change("variant_index",Number(e.target.value))}>
     {(selectedCopy?.variants||[]).map((v,i)=><option value={i} key={i}>#{i+1}: {v.headline.slice(0,30)}</option>)}
    </select>
   </label>
   <label className="text-sm">Approved image
    <select className={cls} value={form.media_id} onChange={e=>change("media_id",e.target.value)}>
     <option value="">Choose approved image</option>
     {approvedImages.map(m=><option key={m.id} value={m.id}>{m.aspect} · {m.id.slice(0,12)}</option>)}
    </select>
   </label>
   <label className="text-sm">Proposed daily budget (USD, not activated)
    <input className={cls} type="number" min="5" max="500" step="1"
     value={form.daily_budget_cents/100} onChange={e=>change("daily_budget_cents",Math.round(Number(e.target.value)*100))}/>
   </label>
   <label className="text-sm">Reviewed HTTPS destination
    <input className={cls} type="url" value={form.destination_url}
     placeholder="https://your-business.example/final-expense" onChange={e=>change("destination_url",e.target.value)}/>
   </label>
   <label className="text-sm">Advertising compliance review reference
    <input className={cls} value={form.review_reference} maxLength={200}
     onChange={e=>change("review_reference",e.target.value)}/>
   </label>
  </div>
  {(!approvedCopy.length||!approvedImages.length)&&<p className="text-xs text-amber-800">First approve channel copy in Content Studio and an image in Visual Studio.</p>}
  {!bindingsHere.length&&<p className="text-xs text-amber-800">Connect an approved Meta form in Receive Leads first.</p>}
  <div className="flex flex-wrap gap-2">
   <button className={btn} disabled={!ready||busy} type="button" onClick={preview}
    data-testid="meta-ad-preview">Preview without Meta writes</button>
   <button className={btn+" bg-slate-900 text-white"} disabled={!ready||!plan||busy||!cap?.configured||!cap?.staging_enabled}
    type="button" onClick={stage} data-testid="meta-ad-paused-stage">Create PAUSED Meta leads ad</button>
  </div>
  {plan&&<div className="rounded-lg border bg-slate-50 p-3 text-sm" data-testid="meta-ad-plan">
   <strong>{plan.campaign_name} · OUTCOME_LEADS</strong>
   <p>Approved Form {plan.lead_form_id} · Financial Products and Services · All resources PAUSED</p>
   <p>Budget if subsequently activated: {"$"+(plan.daily_budget_cents/100).toFixed(2)} per day</p>
  </div>}
  {stages.length>0&&<div className="space-y-2 border-t pt-3">
   <h3 className="font-semibold">Provider staging history</h3>
   {stages.map(s=><div className="rounded border p-2 text-xs" key={s.id}>
    <strong>{s.status}</strong> · Campaign {s.provider?.campaign_id||"not yet created"} · Ad {s.provider?.ad_id||"not yet created"}
    {s.status==="failed_manual_review"&&<p className="text-amber-700">Partial failure. Inspect Meta resource IDs before retrying; no automatic replay.</p>}
   </div>)}
  </div>}
 </section>;
}
